import { Button } from '~/components/general/Button'
import { trackEvent } from '~/utils/tracking'

import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

import { useEffect } from 'react'
import { createOptimizedContentfulImage } from '~/libs/contentful/helpers'
import { FlexContainer } from '~/components/Grid'
import { TypeHomePageBlock } from '~/types/contentful'
import { Asset } from 'contentful'

gsap.registerPlugin(ScrollTrigger)

type FindNewJobProps = {
  section: TypeHomePageBlock<'WITHOUT_UNRESOLVABLE_LINKS', string>
}

export const FindNewJob = ({ section }: FindNewJobProps) => {
  const data = section.fields

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          start: 'top center',
          trigger: '.animate-land-trigger',
        },
      })
      tl.fromTo(
        '.animated-fly-man',
        0.8,
        {
          scale: 2,
          y: -120,
          x: -100,
        },
        {
          x: 0,
          y: 0,
          scale: 1,
        },
      )
        .fromTo(
          '.animated-fly-target',
          0.8,
          {
            scale: 0.7,
            y: -5,
            x: -10,
          },
          {
            scale: 1,
            y: 0,
            x: 0,
          },
          '-=0.6',
        )
        .fromTo(
          '.animated-land-card',
          0.6,
          {
            y: -20,
            x: -20,
            opacity: 0,
          },
          {
            y: 0,
            x: 0,
            opacity: 1,
          },
          '-=0.8',
        )
    })

    return () => ctx.revert()
  }, [])

  const handleCTABtnClick = () => {
    trackEvent('homepage_click_cta', {
      block: data.blockId,
      target: data.buttonLink as string,
      homepage_CTA: data.buttonName as string,
    })
  }

  return (
    <>
      <section
        data-block-id={data.blockId}
        className="max-md:my-0 max-md:-mx-5 max-md:p-[44px_16px_38px] max-lg:p-[44px_34px_60px] max-lg:my-0 max-lg:-mx-5 m-[100px_auto] max-xl:p-[90px_44px_80px] max-xl:m-[135px_auto_100px] relative overflow-hidden rounded-[40px] max-lg:h-auto h-[622px] max-xl:h-[577px] p-[117px_44px_85px]"
        id={data.blockId}
        style={{
          backgroundImage: data.images
            ? `url(${createOptimizedContentfulImage(
                {
                  file: (data.images[0] as Asset<'WITHOUT_UNRESOLVABLE_LINKS', string>).fields
                    .file!,
                },
                {
                  quality: 80,
                  width: 350,
                },
              )})`
            : '',
          backgroundSize: 'cover',
          maxWidth: '1374px',
        }}
      >
        <FlexContainer className="max-lg:px-6 h-full max-lg:h-auto max-lg:flex-wrap max-lg:flex-col-reverse max-lg:py-0">
          <FlexContainer
            justify={'start'}
            className="max-w-[364px] max-lg:max-w-[514px] max-lg:my-0 max-lg:mx-auto z-2 relative ml-0 max-lg:flex-col-reverse"
          >
            <FlexContainer
              justify={'between'}
              className="flex-col max-xl:items-stretch  max-xl:!justify-center relative items-start"
            >
              <h2 className="mb-10 max-xl:mb-8 max-md:mb-5 text-2xl">{data.title}</h2>
              <p className="text-s mb-10 max-xl:mb-9 max-md:mb-4">{data.description}</p>
              <Button
                text={data.buttonName}
                type={'link'}
                href={data.buttonLink}
                onClick={handleCTABtnClick}
                className={'w-fit'}
              />
            </FlexContainer>
          </FlexContainer>

          <div
            className={
              'relative h-full min-w-[600px] max-lg:my-0 max-lg:mx-auto max-md:min-w-full max-md:min-h-[262px] max-xl:min-w-[514px] max-xl:-ml-50 max-lg:min-w-[367px] max-lg:min-h-100 animate-land-trigger'
            }
          >
            <img
              className="animated-fly-man max-xl:h-auto max-md:w-[30vw] max-xl:w-[15vw] w-47 h-54"
              src="/images/animate/land/land_man.svg"
              alt="Career platform Career.io"
              loading="lazy"
              style={{ position: 'absolute', right: '36%', bottom: '14%', zIndex: 1 }}
            />
            <img
              className="animated-fly-target absolute w-17 max-md:w-11"
              src="/images/animate/land/land_figure.svg"
              alt="Career platform Career.io"
              loading="lazy"
              style={{ height: 43, bottom: '3%', right: '34.3%' }}
            />

            <img
              className="w-45 h-55 animated-land-card opacity-0 absolute max-xl:h-auto max-md:w-[25vw] max-xl:w-[15vw]"
              src="/images/animate/land/land_card_1.svg"
              alt="Career platform Career.io"
              loading="lazy"
              style={{ right: '62.6%', bottom: '41%' }}
            />
            <img
              className="w-45 h-55 animated-land-card opacity-0 absolute max-xl:h-auto max-md:w-[25vw] max-xl:w-[15vw]"
              src="/images/animate/land/land_card_2.svg"
              alt="Career platform Career.io"
              loading="lazy"
              style={{ right: '31.5%', bottom: '63%' }}
            />
            <img
              className="w-45 h-55 animated-land-card opacity-0 absolute max-xl:h-auto max-md:w-[25vw] max-xl:w-[15vw]"
              src="/images/animate/land/land_card_3.svg"
              alt="Career platform Career.io"
              loading="lazy"
              style={{ right: '0.5%', bottom: '44%' }}
            />
          </div>
        </FlexContainer>
      </section>
    </>
  )
}
